import { useFrame, useThree } from "@react-three/fiber";
import { useScroll } from "@react-three/drei";
import * as React from "react";
import { CatmullRomCurve3, Vector3 } from "three";
import { STATIC_CAMERA_TARGET } from "./constants";
import { clamp01 } from "./utils";
import { useSceneScalars } from "../../hooks/useSceneScalars";

export type SceneKey = "home" | "services" | "decks" | "reviews";

type Props = {
  onSceneChange: (scene: SceneKey) => void;
};

export function ScrollSceneController({ onSceneChange }: Props) {
  const scroll = useScroll();
  const scalars = useSceneScalars();
  const { camera } = useThree();
  const lastSceneRef = React.useRef<SceneKey | null>(null);
  const vPos = React.useRef(new Vector3());
  const vTarget = React.useRef(new Vector3());

  const HOME = React.useMemo(
    () => ({
      pos: scalars.staticCamera,
      target: STATIC_CAMERA_TARGET,
    }),
    [scalars.staticCamera],
  );
  const SERVICES = scalars.servicesCamera;
  const SERVICES_END = scalars.servicesEndCamera;
  const DECKS = scalars.decksCamera;

  const GATE_PASS_POS = React.useMemo(() => new Vector3(-100, 40, 0), []);
  const GATE_PASS_TARGET = React.useMemo(() => new Vector3(-100, 40, 0), []);

  // Camera path as CatmullRomCurve3: HOME -> GATE_PASS -> SERVICES -> SERVICES_END -> DECKS.
  // 5 control points → 4 segments. Scroll percentage maps to curve parameter u in [0, 1].
  const positionCurve = React.useMemo(() => {
    const points = [
      new Vector3(HOME.pos[0], HOME.pos[1], HOME.pos[2]),
      GATE_PASS_POS.clone(),
      new Vector3(SERVICES.pos[0], SERVICES.pos[1], SERVICES.pos[2]),
      new Vector3(
        SERVICES_END.pos[0],
        SERVICES_END.pos[1],
        SERVICES_END.pos[2],
      ),
      new Vector3(DECKS.pos[0], DECKS.pos[1], DECKS.pos[2]),
    ];
    return new CatmullRomCurve3(points);
  }, [HOME.pos, SERVICES.pos, SERVICES_END.pos, GATE_PASS_POS, DECKS.pos]);

  const targetCurve = React.useMemo(() => {
    const points = [
      new Vector3(HOME.target[0], HOME.target[1], HOME.target[2]),
      GATE_PASS_TARGET.clone(),
      new Vector3(SERVICES.target[0], SERVICES.target[1], SERVICES.target[2]),
      new Vector3(
        SERVICES_END.target[0],
        SERVICES_END.target[1],
        SERVICES_END.target[2],
      ),
      new Vector3(DECKS.target[0], DECKS.target[1], DECKS.target[2]),
    ];
    return new CatmullRomCurve3(points);
  }, [
    HOME.target,
    SERVICES.target,
    SERVICES_END.target,
    GATE_PASS_TARGET,
    DECKS.target,
  ]);

  useFrame(() => {
    const t = Math.min(scroll.offset * scalars.tScale, scalars.tMax);

    let scene: SceneKey = "home";
    if (t >= scalars.thresholds.decksReviews) scene = "reviews";
    else if (t >= scalars.thresholds.servicesDecks) scene = "decks";
    else if (t >= scalars.thresholds.homeServices) scene = "services";

    if (scene !== lastSceneRef.current) {
      lastSceneRef.current = scene;
      onSceneChange(scene);
    }

    // Map section t to curve parameter u in [0, 1].
    // 5 points → 4 segments: HOME(0) → GATE(0.25) → SERVICES(0.5) →
    // SERVICES_END(0.75) → DECKS(1). For t >= scalars.thresholds.decksDwell
    // (decks dwell + reviews) the camera is parked at DECKS — reviews has no
    // 3D content of its own, so the camera position is irrelevant past t=7
    // and we just clamp to 1.
    const U_AT_SERVICES_START = 0.5;
    const U_AT_SERVICES_END = 0.75;
    let u: number;
    if (t <= scalars.thresholds.homeServices) {
      u = (t / scalars.thresholds.homeServices) * U_AT_SERVICES_START;
    } else if (t < scalars.thresholds.servicesDecks) {
      u =
        U_AT_SERVICES_START +
        ((t - scalars.thresholds.homeServices) /
          (scalars.thresholds.servicesDecks - scalars.thresholds.homeServices)) *
          (U_AT_SERVICES_END - U_AT_SERVICES_START);
    } else if (t < scalars.thresholds.decksDwell) {
      // SERVICES_END → DECKS flight. Ease the segment progress with
      // smoothstep so camera velocity is 0 at both ends: it ramps up gently
      // from the slow services crawl (no speed jump at SERVICES_DECKS) and
      // decelerates into the parked DECKS pose (no hard stop). On scroll-up
      // this is what stops the camera "shooting out" of the parked position.
      // u=1 at t=DWELL is unchanged, so the decks framing stays identical.
      const f =
        (t - scalars.thresholds.servicesDecks) /
        (scalars.thresholds.decksDwell - scalars.thresholds.servicesDecks);
      const eased = f * f * (3 - 2 * f);
      u = U_AT_SERVICES_END + eased * (1 - U_AT_SERVICES_END);
    } else {
      // t in [scalars.thresholds.decksDwell, scalars.tMax] — camera parked at
      // DECKS through dwell + reviews.
      u = 1;
    }
    u = clamp01(u);

    positionCurve.getPoint(u, vPos.current);
    targetCurve.getPoint(u, vTarget.current);

    // Drive camera directly from scroll position so the animation is perfectly reversible
    // (scrolling up plays the same path backwards, like a video timeline).
    camera.position.copy(vPos.current);
    camera.lookAt(vTarget.current);
    camera.updateMatrixWorld();
  });

  return null;
}
